import _, { concat, findIndex, includes, indexOf, intersection, map, noop, nth, times, without } from 'lodash';
import { nanoid } from 'nanoid';
import React, { ChangeEventHandler, createContext, MouseEventHandler, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { FieldArrayMethodProps, useFormContext, useWatch } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import { Button, Navbar, Table } from 'reactstrap';
import { usePackagings } from '../../data/packagings';
import { useUnNumbers } from '../../data/unNumbers';
import styled from '../../themes/themed-styled-components';
import { DeepPartialGraph, DeepPartialNotListed } from '../../types';
import { DangerousGoods, DangerousGoodsInput } from '../../types/graph';
import { HorizontalScroll } from '../ui';

const DangerousGoodsTable = styled(Table)`
thead, tbody {
    tr {
        td, th {
            vertical-align: middle;
            text-align: center;

            label {
                padding: .25em .5em;
                margin: 0;

                input[type:'checkbox'] {
                    margin: 0;
                }
            }

            input.form-control {
                min-width: 5em;
            }
        }
    }
}
`

const DangerousGoodsTableHead = styled.thead<{ top?: number }>`
z-index: 10;
position: sticky;

top: ${({ top }) => top ?? 46}px;
::before {
    z-index: -1;
    content: "";
    position: absolute;
    height: 100%;
    width: 100%;
    background-color: ${({ theme: { borderColor } }) => borderColor};
}
tr {
    z-index: 10;
    background-color: #fff;
}
`

const SelectionCell = styled.td`
position: sticky;
left: 0;
z-index: 5;

::before {
    z-index: -20;
    content: "";
    position: absolute;
    height: 100%;
    width: 100%;
    background-color: #fff;
    top: 0;
    left: 0;
}

tbody tr:nth-of-type(odd) &::after {
    z-index: -10;
    content: "";
    position: absolute;
    height: 100%;
    width: 100%;
    background-color: rgba(0, 0, 0, 0.05);
    top: 0;
    left: 0;
}
`

//const ActionCell = styled.td`
//position: sticky;
//right: 0;
//z-index: 5;

//::before {
//    z-index: -20;
//    content: "";
//    position: absolute;
//    height: 100%;
//    width: 100%;
//    background-color: #fff;
//    top: 0;
//    left: 0;
//}

//tbody tr:nth-of-type(odd) &::after {
//    z-index: -10;
//    content: "";
//    position: absolute;
//    height: 100%;
//    width: 100%;
//    background-color: rgba(0, 0, 0, 0.05);
//    top: 0;
//    left: 0;
//}
//`

//type PathTo<TFieldValues extends FieldValues = FieldValues, TFieldType = any, TFieldArrayName extends ArrayPath<TFieldValues> = ArrayPath<TFieldValues>> = PathValue<TFieldValues, TFieldArrayName> extends TFieldType ? TFieldArrayName : never;

export type DangerousGoodsFieldValue = DeepPartialNotListed<DangerousGoodsInput, 'id'>

// @ts-ignore
export const dangerousGoodsToFieldValueWithKey: (dangerousGoods: DeepPartialGraph<DangerousGoods>) => DangerousGoodsFieldValue & { key: string } = ({
    id, unNumber, packingGroup, packaging, type, weight, numberOfPackages, provision274, flashPoint, adrClass, adrSecondaryClasses,
    marinePollutant, limitedQuantity, fumigated, coolingAgents, elevatedTemperature }) => {

    return {
        key: id?.toString() ?? nanoid(),
        id: id ?? 0,
        unNumber: unNumber ?? '',
        packingGroup: packingGroup ?? '',
        packaging: packaging ?? null,
        type: type ?? null,
        weight: weight ?? null,
        numberOfPackages: numberOfPackages ?? null,
        provision274: provision274 ?? null,
        flashPoint: flashPoint ?? null,
        adrClass: adrClass ?? null,
        adrSecondaryClasses: adrSecondaryClasses ?? null,
        marinePollutant: marinePollutant ?? null,
        limitedQuantity: limitedQuantity ?? null,
        fumigated: fumigated ?? null,
        coolingAgents: coolingAgents ?? null,
        elevatedTemperature: elevatedTemperature ?? null
    }
}

type DangerousGoodsFieldsProps = {
    formId: string,
    name: string,
    swap: (indexA: number, indexB: number) => void,
    move: (indexA: number, indexB: number) => void,
    prepend: (value: DangerousGoodsFieldValue | DangerousGoodsFieldValue[], options?: FieldArrayMethodProps) => void,
    append: (value: DangerousGoodsFieldValue | DangerousGoodsFieldValue[], options?: FieldArrayMethodProps) => void,
    remove: (index?: number | number[]) => void,
    insert: (index: number, value: DangerousGoodsFieldValue | DangerousGoodsFieldValue[], options?: FieldArrayMethodProps) => void,
    fields: Array<{ key: string } & DangerousGoodsFieldValue>,
    disabled?: boolean
};

export type DangerousGoodsTableFormContextObject = {
    id: string,
    onFieldSelectionChange: (name: string, selected: boolean) => void,
    onFieldRemoveAttempt: (name: string) => void,
    disabled: boolean
    //packagingsData: DeepPartialGraph<HacpackQuery>|null|undefined
};

const context = createContext<DangerousGoodsTableFormContextObject>({
    id: nanoid(),
    onFieldSelectionChange: noop,
    onFieldRemoveAttempt: noop,
    disabled: false
    //packagingsData: undefined
});

export const useDangerousGoodsFormTable = () => useContext(context)

export const DangerousGoodsFormTable = ({ formId: id, name, append, remove, fields, disabled }: DangerousGoodsFieldsProps) => {

    //const { getValues } = useFormContext();
    //const { data: unNumbersData } = useUnNumbers();

    //const unNumbersDataList = useMemo(() => <datalist id={`unNumberDatalist${id}`}>{
    //    _(unNumbersData?.unNumbers).uniqBy('un').map(({ id, un }) => <option key={id} value={un} />).value()
    //}</datalist>, [unNumbersData]);

    const [selectedKeys, setSelectedKeys] = useState<string[]>([]);
    const selectCheckboxRef = useRef<HTMLInputElement>(null);
    const { t } = useTranslation('common');

    const [totalCount, selectedCount] = useMemo(() => {
        const selected = selectedKeys.length;
        const total = fields.length;

        return [total, selected]
    }, [selectedKeys, fields])

    const names = useMemo(() => map(fields, (f, index) => `${name}.${index}`), [fields, name]);

    useEffect(() => {
        const keys = map(fields, 'key');
        setSelectedKeys(selectedKeys => {
            const intr = intersection(keys, selectedKeys);

            if (intr.length === selectedKeys.length) {
                return selectedKeys;
            }

            return intr;
        });
    }, [fields, setSelectedKeys]);

    useEffect(() => {
        const selectCheckbox = selectCheckboxRef.current;

        if (selectCheckbox === null) {
            return;
        }

        if (totalCount === 0) {
            selectCheckbox.checked = false;
            selectCheckbox.indeterminate = false;
            selectCheckbox.disabled = true;
            return;
        }

        const checked = selectedCount === totalCount;

        selectCheckbox.disabled = false;
        selectCheckbox.checked = checked;
        selectCheckbox.indeterminate = checked === false && selectedCount !== 0;

    }, [selectedKeys, totalCount, selectedCount]);

    const fieldsHeadings = useMemo(() => [
        <th key="unNumber">{t('UN')}</th>,
        <th key="adrClass">{t('Classe')}</th>,
        <th key="packingGroup">{t('Packing Group')}</th>,
        /*<th>{t('Proper Shipping Name')}</th>*/
        <th key="limitedQuantity">{t('Limited Quantity')}</th>,
        <th key="packaging">{t('Tipo d\'imballaggio')}</th>,
        <th key="weight">{t('Peso (kg)')}</th>,
        <th key="numberOfPackages">{t('Numero di colli')}</th>,
        <th key="flashPoint">{t('Flash Point')}</th>
    ], [t])

    const dataColsCount = fieldsHeadings.length;

    const tableColumns = useMemo(() => <colgroup>
        {times(dataColsCount, (i) => <col key={i} />)}
    </colgroup>, [dataColsCount])

    const onFieldSelectionChange = useCallback((name: string, selected: boolean) => {
        const key = nth(fields, indexOf(names, name))?.key;

        if (key === undefined) {
            return;
        }

        setSelectedKeys((selectedKeys) => {
            if (selected === true) {
                return concat(selectedKeys, key)
            }
            return without(selectedKeys, key)
        })
    }, [setSelectedKeys, names, fields]);

    const onFieldRemoveAttempt = useCallback((name: string) => {
        const index = indexOf(names, name);

        if (index < 0) {
            return;
        }

        remove(index);
    }, [names, remove]);

    const selectAll = useCallback(() => {
        setSelectedKeys(map(fields, 'key'))
    }, [setSelectedKeys, fields]);

    const selectNone = useCallback(() => {
        setSelectedKeys([])
    }, [setSelectedKeys]);

    const handleAddButtonClick: MouseEventHandler<HTMLButtonElement> = useCallback(() => {
        append(dangerousGoodsToFieldValueWithKey({}));
    }, [append]);

    const handleRemoveButtonClick: MouseEventHandler<HTMLButtonElement> = useCallback(() => {
        remove(map(selectedKeys, (key) => findIndex(fields, { key })));
    }, [remove, fields, selectedKeys]);

    const handleSelectCheckboxChange: ChangeEventHandler<HTMLInputElement> = useCallback(({ target: { checked } }) => {
        if (checked === false) {
            selectNone();
            return;
        }
        selectAll();
    }, [selectAll, selectNone]);

    const { data: packagingsData } = usePackagings();

    //const packagingOptions = useMemo(() => {
    //    if (packagingsData === undefined || packagingsData === null) {
    //        return null;
    //    }

    //    const { packagings, extraPmisPackagings } = packagingsData;

    //    return <>
    //        <>{map(extraPmisPackagings, ({ id, name }) => <option key={id} value={name}>{name}</option>)}</>
    //        <>{map(packagings, ({ id, fullName }) => <option key={id} value={fullName}>{fullName}</option>)}</>
    //    </>;
    //}, [packagingsData])

    const header = useMemo(() => <Navbar className="navbar ng-white box-shadow" style={{ padding: 0, zIndex: 40, position: 'sticky', top: 0, left: 0, backgroundColor: '#fff' }} light>
        <HorizontalScroll>
            <div style={{ display: 'flex', flexWrap: 'nowrap', width: 'auto', minWidth: '100%', gap: '1em' }}>
                <div style={{ flex: 0, display: 'flex', flexWrap: 'nowrap', alignItems: 'center', padding: '4px 0 4px 8px' }}>
                    <span className="text-nowrap" style={{ lineHeight: '38px' }}>
                        <strong>{t('Merci pericolose')}</strong>
                    </span>
                </div>

                <div style={{ display: 'flex', flex: 1, flexBasis: 'auto', alignItems: 'center', padding: '4px 0' }}>
                    <div style={{ display: 'flex', flexWrap: 'nowrap', gap: '.5em' }}>
                        <Button type='button' disabled={disabled} onClick={handleAddButtonClick} style={{ whiteSpace: 'nowrap' }}>{t('Aggiungi')}</Button>

                        {/*<div style={{ display: 'flex', flexWrap: 'nowrap', gap: '.5em', alignItems: 'center', flexShrink: 0 }}>*/}
                        {/*    <Label style={{ whiteSpace: 'nowrap', margin: 0 }} for={shipInputField.id}>{t('Nave')}</Label>*/}
                        {/*    <input type="text" disabled={fetching || showResult} {...shipInputField} />*/}
                        {/*</div>*/}
                        {/*<div style={{ display: 'flex', flexWrap: 'nowrap', gap: '.5em', alignItems: 'center', flexShrink: 0 }}>*/}
                        {/*    <Label style={{ whiteSpace: 'nowrap', margin: 0 }} for={arrivalInputField.id}>{t('Previsto arrivo')}</Label>*/}
                        {/*    <input type="date" disabled={fetching || showResult} {...arrivalInputField} />*/}
                        {/*</div>*/}
                    </div>
                </div>

                <div style={{ display: 'flex', flex: 0, flexWrap: 'nowrap', alignItems: 'center', gap: '.5em', padding: '4px 8px 4px 0' }}>
                    {selectedCount === 0 ? null : <>
                        <span className="text-nowrap" style={{ lineHeight: '38px' }}>
                            <Trans i18nKey='selectedCount' t={t} count={selectedCount}>{{ selectedCount }} selezionata</Trans>
                        </span>
                        <Button type='button' color='danger' onClick={handleRemoveButtonClick} style={{ whiteSpace: 'nowrap' }}>{t('Elimina')}</Button>
                    </>}
                    {/*<Button type="submit" color="primary" className="form-control" disabled={fetching || showResult || files === undefined}>{t('Salva')}</Button>*/}
                    {/*<Button className="ml-sm-2 form-control" onClick={handleCancelButtonClick} disabled={cancelDisabled}>{t('Annulla')}</Button>*/}
                </div>
            </div>
        </HorizontalScroll>
    </Navbar>, [t, selectedCount, disabled, handleAddButtonClick, handleRemoveButtonClick]);

    const tableHead = useMemo(() => <DangerousGoodsTableHead>
        <tr>
            <SelectionCell as="th">
                <input onChange={handleSelectCheckboxChange} ref={selectCheckboxRef} type='checkbox' />
            </SelectionCell>
            {fieldsHeadings}
        </tr>
    </DangerousGoodsTableHead>, [handleSelectCheckboxChange, fieldsHeadings, selectCheckboxRef])

    const tableBody = useMemo(() => <tbody>
        {fields.map((data, index) => {
            const { key } = data;

            const selected = includes(selectedKeys, key);
            const name = names[index];

            return <DangerousGoodsFormTableRow
                key={key}
                name={name}
                value={data}
                selected={selected}
            />
        })}
    </tbody>, [fields, selectedKeys, names])

    const packagingDatalist = useMemo(() => {
        if (packagingsData === undefined || packagingsData === null) {
            return null;
        }

        const { packagings, extraPmisPackagings } = packagingsData;

        return <datalist id={`packagingOptions_${id}`}>
            <>{map(extraPmisPackagings, ({ id, name }) => <option key={id} value={name} />)}</>
            <>{map(packagings, ({ id, fullName }) => <option key={id} value={fullName} />)}</>
        </datalist>;
    }, [packagingsData, id])

    return <context.Provider value={{ onFieldSelectionChange, onFieldRemoveAttempt, id, disabled: disabled ?? false }}>
        {header}
        <fieldset disabled={disabled ?? false}>
            {/*<GridLayout header={header}>*/}
            {fields.length > 0 ?
                <DangerousGoodsTable striped>
                    {tableColumns}
                    {tableHead}
                    {tableBody}
                </DangerousGoodsTable> : null}
            {/*</GridLayout>*/}
        </fieldset>
        {packagingDatalist}
    </context.Provider>
}

type DangerousGoodsRowProps = {
    name: string,
    value: DangerousGoodsFieldValue,
    selected: boolean
}

const DangerousGoodsFormTableRow = ({ name, value, selected }: DangerousGoodsRowProps) => {

    const [unNumberWatch, adrClassWatch, packingGroupWatch] = useWatch({ name: [`${name}.unNumber`, `${name}.adrClass`, `${name}.packingGroup`] }) as [string | undefined, string | null | undefined, string | null | undefined];

    const { t } = useTranslation('common');

    const form = useFormContext();

    const { id, onFieldSelectionChange } = useDangerousGoodsFormTable();

    const { register, unregister, setValue } = form;

    const { data/*, fetching*/ } = useUnNumbers({ unNumbers: unNumberWatch?.length === 4 ? [unNumberWatch] : null })

    const unNumbers = useMemo(() => data?.unNumbers ?? [], [data]);
    const unNumberNotValid = unNumberWatch?.length === 4 && unNumbers.length === 0;

    const hazardClassOptions = useMemo(() => _(unNumbers).map('hazardCls').uniq().value(), [unNumbers])

    const unNumberIsDefault = unNumberWatch === value.unNumber;
    const hazardClassIsDefault = unNumberIsDefault && adrClassWatch === value.adrClass;

    const packingGroupOptions = useMemo(() => {
        if (adrClassWatch === undefined || adrClassWatch === null) {
            return [];
        }

        return _(unNumbers).filter({ hazardCls: adrClassWatch }).map('packingGroup').uniq().value()
    }, [unNumbers, adrClassWatch])

    useEffect(() => {
        if (includes(hazardClassOptions, adrClassWatch) === true) {
            return;
        }

        if (hazardClassOptions.length > 0) {
            if (unNumberIsDefault && includes(hazardClassOptions, value.adrClass)) {
                setValue(`${name}.adrClass`, value.adrClass);
                return;
            }
            setValue(`${name}.adrClass`, hazardClassOptions[0]);
            return;
        }

        if (adrClassWatch !== null) {
            setValue(`${name}.adrClass`, null);
        }
    }, [hazardClassOptions, adrClassWatch, unNumberIsDefault, setValue, value, name])

    useEffect(() => {
        if (includes(packingGroupOptions, packingGroupWatch) === true) {
            return;
        }

        if (packingGroupOptions.length > 0) {
            if (hazardClassIsDefault && includes(packingGroupOptions, value.packingGroup)) {
                setValue(`${name}.packingGroup`, value.packingGroup);
                return;
            }
            setValue(`${name}.packingGroup`, packingGroupOptions[0]);
            return;
        }

        if (packingGroupWatch !== null) {
            setValue(`${name}.packingGroup`, null);
        }
    }, [packingGroupOptions, packingGroupWatch, setValue, value, hazardClassIsDefault, name])

    useEffect(() => {
        return () => {
            unregister(name)
        }
    }, [unregister, name]);

    const handleSelectionChange: ChangeEventHandler<HTMLInputElement> = useCallback(({ target: { checked } }) => {
        if (selected === checked) {
            return;
        }
        onFieldSelectionChange(name, checked);
    }, [name, selected, onFieldSelectionChange])

    const selectionCell = useMemo(() => {
        return <SelectionCell key="selection">
            <label>
                <input checked={selected} type='checkbox' onChange={handleSelectionChange} />
            </label>
        </SelectionCell>;
    }, [selected, handleSelectionChange])

    const fieldCells = useMemo(() => {
        // @ts-ignore
        return <>
            <td key="unNumber">
                <div className='d-none'>
                    <input type='hidden' value={value.id} {...register(`${name}.id`, {
                        valueAsNumber: true
                    })} />
                    <input type='hidden' value={(value.adrSecondaryClasses ?? []) as string[]} {...register(`${name}.adrSecondaryClasses`)} />
                </div>

                <input type='text' defaultValue={value.unNumber} placeholder='0000' className={'form-control'} style={{ width: '4em' }} {...register(`${name}.unNumber`, {

                    pattern: { value: /^[0-9]{4}$/, message: t('Formato non corretto') },
                    required: t('Campo obbligatorio').toString(),
                    validate: (v) => {
                        console.log({ v, unNumberNotValid });
                        return unNumberNotValid === false ? true : t('UN non trovato').toString()
                    }
                })} />
            </td>
            <td key="adrClass">
                {hazardClassOptions.length > 1
                    ? <select style={{ width: '6em' }} defaultValue={value.adrClass} className={'form-control'} {...register(`${name}.adrClass`)}>
                        {hazardClassOptions.map(hazardClass => <option key={hazardClass} value={hazardClass}>{hazardClass}</option>)}
                    </select>
                    : <input style={{ width: '6em' }} defaultValue={value.adrClass} readOnly className={'form-control'} {...register(`${name}.adrClass`)} />
                }
            </td>
            <td key="packingGroup">
                {packingGroupOptions.length > 1
                    ? <select style={{ width: '5em' }} defaultValue={value.packingGroup} className={'form-control'} {...register(`${name}.packingGroup`)}>
                        {packingGroupOptions.map(packingGroup => <option key={packingGroup} value={packingGroup}>{packingGroup}</option>)}
                    </select>
                    : <input style={{ width: '5em' }} defaultValue={value.packingGroup} readOnly className={'form-control'} {...register(`${name}.packingGroup`,)} />
                }
            </td>
            <td key="limitedQuantity">
                <label>
                    <input defaultChecked={value.limitedQuantity ?? undefined} type='checkbox' {...register(`${name}.limitedQuantity`)} />
                </label>
            </td>
            <td>
                {/*{packagingOptions !== undefined && packagingOptions !== null*/}
                {/*    ? <select style={{ minWidth: '18em' }} className={'form-control'} {...register(`${name}.packaging`, { required: true })}>*/}
                {/*        <option key="" value="" title={t('Seleziona il tipo d\'imballaggio')}>{t('--seleziona il tipo d\'imballaggio--')}</option>*/}
                {/*        {packagingOptions}*/}
                {/*    </select>:*/}
                <input style={{ minWidth: '18em' }} defaultValue={value.packaging ?? undefined} list={`packagingOptions_${id}`} className={'form-control'} {...register(`${name}.packaging`, { required: true })} />
                {/*}*/}
            </td>
            <td><input style={{ minWidth: '7em' }} type='number' className={'form-control'} defaultValue={value.weight ?? undefined} {...register(`${name}.weight`, {
                required: t('Campo obbligatorio') as string,
                valueAsNumber: true
            })} /></td>
            <td><input type='number' className={'form-control'} defaultValue={value.numberOfPackages ?? undefined} {...register(`${name}.numberOfPackages`, {
                required: t('Campo obbligatorio') as string,
                valueAsNumber: true
            })} /></td>
            <td><input type='text' className={'form-control'} defaultValue={value.flashPoint ?? undefined} {...register(`${name}.flashPoint`)} /></td>
            {/*<td><Button color="danger" type="button" onClick={() => onFieldRemoveAttempt(name)}><FaTrash /></Button></td>*/}
        </>
    }, [value, name, id, hazardClassOptions, packingGroupOptions, register, unNumberNotValid, t])

    return <tr>
        {selectionCell}
        {fieldCells}
    </tr>;
}